/**
 * Utility to parse bank statement files (.ofx and .csv) into a normalized list of transactions.
 */

// Helper to format Date object into YYYY-MM-DD
export function formatDateISO(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Parses date string in common Brazilian or ISO formats to YYYY-MM-DD.
 * Handles: DD/MM/YYYY, DD-MM-YYYY, YYYY-MM-DD, YYYYMMDD, DD/MM/YY
 */
export function parseFlexibleDate(dateStr) {
  if (!dateStr) return '';
  const clean = String(dateStr).trim();

  // OFX format: 20260815...
  if (/^\d{8}/.test(clean)) {
    const y = clean.substring(0, 4);
    const m = clean.substring(4, 6);
    const d = clean.substring(6, 8);
    return `${y}-${m}-${d}`;
  }

  // DD/MM/YYYY or DD-MM-YYYY
  const dmyMatch = clean.match(/^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})/);
  if (dmyMatch) {
    const d = dmyMatch[1].padStart(2, '0');
    const m = dmyMatch[2].padStart(2, '0');
    const y = dmyMatch[3];
    return `${y}-${m}-${d}`;
  }

  // YYYY-MM-DD or YYYY/MM/DD
  const ymdMatch = clean.match(/^(\d{4})[\/\-\.](\d{1,2})[\/\-\.](\d{1,2})/);
  if (ymdMatch) {
    const y = ymdMatch[1];
    const m = ymdMatch[2].padStart(2, '0');
    const d = ymdMatch[3].padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  // DD/MM/YY
  const dmyShortMatch = clean.match(/^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{2})/);
  if (dmyShortMatch) {
    const d = dmyShortMatch[1].padStart(2, '0');
    const m = dmyShortMatch[2].padStart(2, '0');
    const y = `20${dmyShortMatch[3]}`;
    return `${y}-${m}-${d}`;
  }

  return '';
}

/**
 * Parses numeric monetary values from string.
 * Handles Brazilian format "1.250,50", negative signs, currency symbols, and trailing D/C.
 */
export function parseFlexibleAmount(valStr, dcIndicator = '') {
  if (typeof valStr === 'number') return valStr;
  if (!valStr) return 0;

  let str = String(valStr).trim();
  let isNegative = false;

  // Check for leading/trailing minus or parentheses (100.00)
  if (str.startsWith('-') || str.endsWith('-') || (str.startsWith('(') && str.endsWith(')'))) {
    isNegative = true;
  }
  
  // Check D/C indicators
  const upper = (str + ' ' + (dcIndicator || '')).toUpperCase();
  if (/\b(D|DÉBITO|DEBITO|DESPESA|SAIDA|SAÍDA)\b/.test(upper)) {
    isNegative = true;
  }

  // Clean non-numeric characters except comma, dot, minus
  str = str.replace(/[R$\s\(\)]/g, '').replace(/-/g, '').trim();

  // If contains comma as decimal separator: 1.250,50 or 250,50
  if (str.includes(',') && str.includes('.')) {
    // Determine which is decimal based on last occurrence
    const lastComma = str.lastIndexOf(',');
    const lastDot = str.lastIndexOf('.');
    if (lastComma > lastDot) {
      // 1.250,50 -> Brazilian
      str = str.replace(/\./g, '').replace(',', '.');
    } else {
      // 1,250.50 -> US
      str = str.replace(/,/g, '');
    }
  } else if (str.includes(',')) {
    // 250,50 -> 250.50
    str = str.replace(',', '.');
  }

  let num = parseFloat(str) || 0;
  if (isNegative && num > 0) {
    num = -num;
  }
  return round2(num);
}

function round2(val) {
  return Math.round((val + Number.EPSILON) * 100) / 100;
}

/**
 * Parses an OFX string (both SGML and XML variants).
 */
export function parseOfxContent(ofxText) {
  if (!ofxText || typeof ofxText !== 'string') {
    throw new Error('Conteúdo OFX inválido ou vazio.');
  }

  const transactions = [];
  
  // Find all <STMTTRN>...</STMTTRN> or unclosed <STMTTRN> in SGML
  const trnRegex = /<STMTTRN>([\s\S]*?)(?:<\/STMTTRN>|(?=<STMTTRN>|<\/BANKTRANLIST>|<\/CCSTMTTRN>|$))/gi;
  let match;

  const extractTag = (block, tagName) => {
    // Matches <TAG>value or <TAG>value</TAG>
    const regex = new RegExp(`<${tagName}>([^<\r\n]+)(?:<\/${tagName}>)?`, 'i');
    const res = block.match(regex);
    return res ? res[1].trim() : '';
  };

  let index = 0;
  while ((match = trnRegex.exec(ofxText)) !== null) {
    const block = match[1];
    if (!block || !block.trim()) continue;

    const trntype = extractTag(block, 'TRNTYPE');
    const dtpostedRaw = extractTag(block, 'DTPOSTED');
    const trnamtRaw = extractTag(block, 'TRNAMT');
    const fitid = extractTag(block, 'FITID') || `ofx_${Date.now()}_${index}`;
    const checknum = extractTag(block, 'CHECKNUM');
    const memo = extractTag(block, 'MEMO');
    const name = extractTag(block, 'NAME');

    const date = parseFlexibleDate(dtpostedRaw);
    let amount = parseFloat(trnamtRaw.replace(',', '.')) || 0;

    // In case OFX TRNTYPE is DEBIT but amount is positive
    if (trntype.toUpperCase() === 'DEBIT' && amount > 0) {
      amount = -amount;
    } else if (trntype.toUpperCase() === 'CREDIT' && amount < 0) {
      amount = Math.abs(amount);
    }
    amount = round2(amount);

    const description = (name || memo || checknum || `Transação ${trntype}`).trim();
    const type = amount >= 0 ? 'receber' : 'pagar';

    transactions.push({
      id: fitid || `stmt_${index}_${Date.now()}`,
      date: date || formatDateISO(new Date()),
      description: description || 'Transação sem descrição',
      amount: amount,
      value: Math.abs(amount),
      type: type,
      source: 'ofx',
      raw: { trntype, dtpostedRaw, trnamtRaw, fitid, memo, name, checknum }
    });
    index++;
  }

  if (transactions.length === 0) {
    throw new Error('Nenhuma transação encontrada no arquivo OFX. Verifique se o extrato contém movimentações.');
  }

  return transactions;
}

/**
 * Parses CSV text with automatic column detection and delimiter detection.
 */
export function parseCsvContent(csvText) {
  if (!csvText || typeof csvText !== 'string') {
    throw new Error('Conteúdo CSV inválido ou vazio.');
  }

  const lines = csvText
    .split(/\r?\n/)
    .map(l => l.trim())
    .filter(l => l.length > 0);

  if (lines.length < 2) {
    throw new Error('O arquivo CSV deve conter um cabeçalho e pelo menos uma linha de transação.');
  }

  // Detect delimiter using the first 5 lines
  const sample = lines.slice(0, 5).join('\n');
  const countSemicolon = (sample.match(/;/g) || []).length;
  const countComma = (sample.match(/,/g) || []).length;
  const countTab = (sample.match(/\t/g) || []).length;

  let delimiter = ';';
  if (countTab > countSemicolon && countTab > countComma) delimiter = '\t';
  else if (countComma > countSemicolon) delimiter = ',';

  // Helper to split CSV line considering quotes
  const splitLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"' || char === "'") {
        inQuotes = !inQuotes;
      } else if (char === delimiter && !inQuotes) {
        result.push(current.trim().replace(/^["']|["']$/g, ''));
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim().replace(/^["']|["']$/g, ''));
    return result;
  };

  // Find header row (some CSVs have metadata rows at the top)
  let headerIndex = 0;
  let dateCol = -1;
  let descCol = -1;
  let valCol = -1;
  let typeCol = -1;

  for (let i = 0; i < Math.min(10, lines.length); i++) {
    const cols = splitLine(lines[i]).map(c => c.toLowerCase());
    
    // Check Date
    const dIdx = cols.findIndex(c => /\b(data|dt|date|data transacao|data do lancamento|data lançamento)\b/i.test(c));
    // Check Description
    const descIdx = cols.findIndex(c => /\b(descricao|descrição|historico|histórico|memo|detalhe|detalhes|lancamento|lançamento|description|estabelecimento)\b/i.test(c));
    // Check Value
    const vIdx = cols.findIndex(c => /\b(valor|quantia|montante|amount|saldo|valor r\$|valor \(r\$\))\b/i.test(c));
    // Check Type / DC
    const tIdx = cols.findIndex(c => /\b(tipo|d\/c|natureza|debito\/credito|débito\/crédito|dc)\b/i.test(c));

    if (dIdx !== -1 && (descIdx !== -1 || vIdx !== -1)) {
      headerIndex = i;
      dateCol = dIdx;
      descCol = descIdx !== -1 ? descIdx : (cols.length > 1 ? 1 : -1);
      valCol = vIdx !== -1 ? vIdx : (cols.length > 2 ? 2 : -1);
      typeCol = tIdx;
      break;
    }
  }

  // Fallback defaults if header matching failed
  if (dateCol === -1 || valCol === -1) {
    const firstRowCols = splitLine(lines[0]);
    if (firstRowCols.length >= 3) {
      headerIndex = 0;
      dateCol = 0;
      descCol = 1;
      valCol = 2;
    } else {
      throw new Error('Não foi possível identificar as colunas de Data e Valor no arquivo CSV.');
    }
  }

  const transactions = [];

  for (let i = headerIndex + 1; i < lines.length; i++) {
    const cols = splitLine(lines[i]);
    if (cols.length <= Math.max(dateCol, valCol)) continue;

    const rawDate = cols[dateCol];
    const rawDesc = descCol !== -1 && cols[descCol] ? cols[descCol] : 'Lançamento extrato';
    const rawVal = cols[valCol];
    const rawType = typeCol !== -1 ? cols[typeCol] : '';

    const date = parseFlexibleDate(rawDate);
    if (!date) continue; // skip non-data or summary lines

    const amount = parseFlexibleAmount(rawVal, rawType);
    if (isNaN(amount) || amount === 0) continue;

    const type = amount >= 0 ? 'receber' : 'pagar';

    transactions.push({
      id: `csv_${i}_${Date.now()}`,
      date: date,
      description: rawDesc || 'Lançamento sem descrição',
      amount: amount,
      value: Math.abs(amount),
      type: type,
      source: 'csv',
      raw: { line: lines[i], cols }
    });
  }

  if (transactions.length === 0) {
    throw new Error('Nenhuma movimentação válida foi extraída do arquivo CSV.');
  }

  return transactions;
}

/**
 * Main function to read and parse a user uploaded File (.ofx or .csv).
 */
export async function parseBankStatementFile(file) {
  if (!file) throw new Error('Nenhum arquivo fornecido.');

  const fileName = file.name.toLowerCase();
  const isOfx = fileName.endsWith('.ofx');
  const isCsv = fileName.endsWith('.csv') || fileName.endsWith('.txt');

  if (!isOfx && !isCsv) {
    throw new Error('Formato de arquivo não suportado. Por favor, envie um arquivo .ofx ou .csv.');
  }

  // Read file as text with UTF-8 first, fallback to ISO-8859-1 if garbled
  const readAsText = (encoding = 'UTF-8') => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Erro ao ler o arquivo.'));
      reader.readAsText(file, encoding);
    });
  };

  let content = await readAsText('UTF-8');
  
  // If UTF-8 contains replacement characters (), re-read as ISO-8859-1 (common in Brazilian banks)
  if (content.includes('')) {
    try {
      content = await readAsText('ISO-8859-1');
    } catch (_e) { /* keep UTF-8 */ }
  }

  if (isOfx) {
    return {
      fileName: file.name,
      fileType: 'OFX',
      fileSize: file.size,
      transactions: parseOfxContent(content)
    };
  } else {
    return {
      fileName: file.name,
      fileType: 'CSV',
      fileSize: file.size,
      transactions: parseCsvContent(content)
    };
  }
}
